import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ModalContext from '../../../../contexts/ModalContext';
import StockContext from '../../../../contexts/StockContext';
import { useTenant } from '../../../../contexts/TenantContext';
import TableFiltersContext from '../../../../contexts/TableFiltersContext';
import { TypeMainOutlet } from '../..';
import ApiService from '../../../../services/ApiService';
import FashionApiService from '../../../../services/FashionApiService';
import { TableContainer } from '../style';
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage';
import {
  FashionProductionMovementRow,
  buildFashionMovementColumns,
  productionMovementScreenKey,
  fashionMovementStatusCode,
  isFashionMovementReverted,
  fashionOpIdFromOrderName,
  insumoResourceIdFromRaw,
  materialCodeFromRaw,
  parseQuantitySuffix,
} from './movementColumns';
import { format } from 'date-fns';

const CELL_EMPTY = '-';

const MOVEMENT_DIRECTION_FILTER_KEY = 'movementDirection';

function getDirectionFilterValues(appliedFilters: any[] | undefined): string[] {
  return (appliedFilters ?? [])
    .filter((f: any) => f.key === MOVEMENT_DIRECTION_FILTER_KEY)
    .map((f: any) => String(f.value || '').toLowerCase().trim())
    .filter(Boolean);
}

function extractAxiosData(response: { data?: unknown } | null | undefined): unknown {
  return response?.data ?? response;
}

async function loadPedidoLabelsForOpIds(opIds: number[]): Promise<Record<number, string>> {
  const out: Record<number, string> = {};
  await Promise.all(
    opIds.map(async (id) => {
      try {
        const res = await FashionApiService.getProductionOrderById(id);
        const data = extractAxiosData(res) as {
          FashionProductionItems?: {
            fashionOrderId: number;
            fashionOrder?: {
              company?: {
                companyName?: string | null;
              };
            };
          }[];
        };
        const items = data?.FashionProductionItems ?? [];
        const clients = [...new Set(items.map((it) => it.fashionOrder?.company?.companyName?.trim()).filter(Boolean))];
        const clientsStr = clients.length ? clients.join(', ') : 'Cliente não informado';
        out[id] = `OP ${id} - ${clientsStr}`;
      } catch {
        out[id] = `OP ${id} - N/A`;
      }
    }),
  );
  return out;
}

async function loadInsumoNames(ids: number[]): Promise<Record<number, string>> {
  const out: Record<number, string> = {};
  await Promise.all(
    ids.map(async (id) => {
      try {
        const res = await ApiService.resources.findById(id);
        const data = extractAxiosData(res) as { material?: string; name?: string; code?: string };
        out[id] = String(data?.code || data?.material || data?.name || `#${id}`);
      } catch {
        out[id] = `Insumo #${id}`;
      }
    }),
  );
  return out;
}

const FashionProductionMovementTable: React.FC = () => {
  const { isAcoTenant } = useTenant();
  const isSteel = isAcoTenant;
  const { setModalActive, setModalSection, setStockMode } = useContext(ModalContext);
  const { setStock } = useContext(StockContext);
  const { quantity, setQuantity, refresh, appliedFilters } = useOutletContext<any>();
  const { search } = useOutletContext<TypeMainOutlet>();
  const { movementTypeFilter } = useContext(TableFiltersContext);
  const [movements, setMovements] = useState<FashionProductionMovementRow[]>([]);
  const [totalMovements, setTotalMovements] = useState<number | null>(null);
  const [lastReturnedCount, setLastReturnedCount] = useState<number>(0);
  const [pedidoByOpId, setPedidoByOpId] = useState<Record<number, string>>({});
  const [insumoNameById, setInsumoNameById] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const normalizeMovements = (raw: any): FashionProductionMovementRow[] => {
    if (Array.isArray(raw)) {
      return raw as FashionProductionMovementRow[];
    }
    if (raw?.data && Array.isArray(raw.data)) {
      return raw.data as FashionProductionMovementRow[];
    }
    if (raw?.items && Array.isArray(raw.items)) {
      return raw.items as FashionProductionMovementRow[];
    }
    if (raw?.results && Array.isArray(raw.results)) {
      return raw.results as FashionProductionMovementRow[];
    }

    if (raw && typeof raw === 'object') {
      const firstArray = Object.values(raw).find((value) => Array.isArray(value)) as FashionProductionMovementRow[] | undefined;
      if (Array.isArray(firstArray)) {
        return firstArray;
      }
    }

    return [];
  };

  useEffect(() => {
    const fetchMovements = async () => {
      setIsLoading(true);
      try {
        const filtersStr = appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined;
        const parameters = {
          size: quantity,
          filters: filtersStr,
          global: search,
          movementType: isSteel ? undefined : movementTypeFilter,
          fashionOnly: !isSteel,
        };
        const responseMovements = await ApiService.movements.find(parameters);
        const rawData = responseMovements?.data;
        const extractedMovements = normalizeMovements(rawData);

        let extractedBuyOrders: any[] = [];
        if (!isSteel) {
          try {
            const responseBuyOrders = await ApiService.buyOrder.findAll();
            extractedBuyOrders = responseBuyOrders?.data || [];
          } catch (err) {
            console.error('Erro ao buscar ordens de compra:', err);
          }
        }

        const unifiedMovements = extractedMovements.map(m => {
          let direction: 'IN' | 'OUT' = 'OUT';
          const oldW = m.oldTotalWeight ?? 0;
          const newW = m.newTotalWeight ?? 0;
          const oldL = m.oldLengthInMm ?? 0;
          const newL = m.newLengthInMm ?? 0;
          const oldQ = m.oldQuantity ?? 0;
          const newQ = m.newQuantity ?? 0;

          if (Math.abs(newW - oldW) > 1e-6) {
            direction = newW > oldW ? 'IN' : 'OUT';
          } else if (Math.abs(newL - oldL) > 1e-6) {
            direction = newL > oldL ? 'IN' : 'OUT';
          } else if (Math.abs(newQ - oldQ) > 1e-6) {
            direction = newQ > oldQ ? 'IN' : 'OUT';
          } else {
            const statusCode = fashionMovementStatusCode(m.status);
            direction = statusCode === '201' ? 'IN' : 'OUT';
          }

          return {
            ...m,
            isBuyOrder: false,
            direction,
          };
        });

        const unifiedBuyOrders = extractedBuyOrders.map(bo => {
          const rawName = bo.StockInProduction?.Stock?.Code?.code || bo.StockInProduction?.Resource?.name || 'Item OC';
          const formattedQty = `${bo.quantityToBuy ?? bo.quantityRequired} ${bo.unitOfMeasurement || ''}`.trim();

          const statusTranslation: Record<string, string> = {
            PENDING: 'Pendente',
            APPROVED: 'Aprovada',
            ORDERED: 'Encomendada',
            DELIVERED: 'Comprada',
            COMPLETED: 'Concluída',
            CANCELLED: 'Cancelada',
          };

          return {
            id: bo.id,
            ploomesOrderId: 0,
            rawMaterialName: bo.StockInProduction?.Resource ? `__INS_RES_${bo.StockInProduction.resourceId}__` : rawName,
            status: bo.status,
            quantity: formattedQty,
            unitOfMeasurement: bo.unitOfMeasurement || '',
            oldTotalWeight: 0,
            newTotalWeight: 0,
            oldLengthInMm: 0,
            newLengthInMm: 0,
            createdAt: bo.createdAt,
            orderName: bo.StockInProduction?.Services?.id ? `OP-FASHION-${bo.StockInProduction.Services.id}|` : 'Ordem de Compra',
            UpdatedByUser: bo.CreatedByUser ? { name: bo.CreatedByUser.name } : { name: 'Sistema' },
            updatedAt: bo.createdAt,
            stockId: bo.StockInProduction?.stockId,
            fashionConsumedStockId: bo.StockInProduction?.stockId,
            fashionConsumedResourceId: bo.StockInProduction?.resourceId,
            isBuyOrder: true,
            buyOrderStatus: statusTranslation[bo.status] || bo.status,
            direction: 'OC',
          };
        });

        let filteredCombined = [...unifiedMovements, ...unifiedBuyOrders];

        if (search && search.trim() !== '') {
          const s = search.toLowerCase().trim();
          filteredCombined = filteredCombined.filter(item => {
            const rawName = String(item.rawMaterialName || '').toLowerCase();
            const displayQty = item.isBuyOrder
              ? String(item.quantity || '')
              : `${item.quantity} ${item.unitOfMeasurement || ''}`;
            const qty = displayQty.toLowerCase();
            const orderName = String(item.orderName || '').toLowerCase();
            const statusLabel = item.isBuyOrder
              ? String(item.buyOrderStatus || '').toLowerCase()
              : (item.status === '410' ? 'desfeito' : 'abatido');
            return rawName.includes(s) || qty.includes(s) || orderName.includes(s) || statusLabel.includes(s);
          });
        }

        if (appliedFilters && appliedFilters.length > 0) {
          const filtersByKey: Record<string, any[]> = {};
          appliedFilters.forEach((f: any) => {
            if (!filtersByKey[f.key]) filtersByKey[f.key] = [];
            filtersByKey[f.key].push(f);
          });

          Object.entries(filtersByKey).forEach(([key, groupFilters]) => {
            filteredCombined = filteredCombined.filter(item => {
              return groupFilters.some((f: any) => {
                const val = String(f.value || '').toLowerCase().trim();
                if (key === MOVEMENT_DIRECTION_FILTER_KEY) {
                  if (val === 'ordem de compra') return item.isBuyOrder === true;
                  if (item.isBuyOrder) return false;
                  const isItemEntry = isSteel
                    ? (item.ploomesOrderId === 0 || item.direction === 'IN')
                    : item.direction === 'IN';
                  if (val === 'entrada') return isItemEntry;
                  if (val === 'saída' || val === 'saida') return !isItemEntry;
                  return true;
                } else if (key === 'status') {
                  if (item.isBuyOrder) {
                    return String(item.status || '').toLowerCase() === val || String(item.buyOrderStatus || '').toLowerCase() === val;
                  } else {
                    if (val === '410' || val === 'desfeito') return item.status === '410';
                    else if (val === 'abatido') return item.status !== '410';
                    return false;
                  }
                } else if (key === 'rawMaterialName') {
                  return String(item.rawMaterialName || '').toLowerCase().includes(val);
                } else if (key === 'origin' || key === 'orderName') {
                  const opId = fashionOpIdFromOrderName(item.orderName);
                  const pedidoLabel = opId != null ? pedidoByOpId[opId] ?? CELL_EMPTY : CELL_EMPTY;
                  const itemOriginLabel = isSteel
                    ? (item.ploomesOrderId && item.ploomesOrderId > 1
                        ? (item.orderName ? String(item.orderName) : 'Pedido')
                        : 'Manual')
                    : (pedidoLabel !== CELL_EMPTY ? `OP - ${pedidoLabel}` : 'Manual');
                  return itemOriginLabel.toLowerCase().includes(val) || String(item.orderName || '').toLowerCase().includes(val);
                } else if (key === 'quantity') {
                  const searchVal = val.toLowerCase().trim();
                  const isPureNumber = /^\d+([.,]\d+)?$/.test(searchVal);
                  if (isPureNumber) {
                    const targetNum = parseFloat(searchVal.replace(',', '.'));
                    if (item.isBuyOrder) {
                      const parsed = parseQuantitySuffix(item.quantity);
                      return parsed ? parsed.num === targetNum : false;
                    } else {
                      return Number(item.quantity) === targetNum;
                    }
                  } else {
                    const match = searchVal.match(/^([\d.,]+)\s*([a-zA-Zºªá-úÁ-Ú%./\-*]+.*)$/);
                    if (match) {
                      const searchNum = parseFloat(match[1].replace(',', '.'));
                      const searchUnit = match[2].trim().toLowerCase();
                      if (item.isBuyOrder) {
                        const parsed = parseQuantitySuffix(item.quantity);
                        return parsed ? parsed.num === searchNum && parsed.suffix.toLowerCase() === searchUnit : false;
                      } else {
                        return Number(item.quantity) === searchNum && String(item.unitOfMeasurement || '').toLowerCase() === searchUnit;
                      }
                    } else {
                      if (item.isBuyOrder) {
                        return String(item.quantity || '').toLowerCase().includes(searchVal);
                      } else {
                        const formatted = `${item.quantity} ${item.unitOfMeasurement || ''}`.toLowerCase();
                        return formatted.includes(searchVal);
                      }
                    }
                  }
                } else if (key === 'createdAt') {
                  if (!item.createdAt) return false;
                  const itemDate = new Date(item.createdAt);
                  if (isNaN(itemDate.getTime())) return false;
                  const itemYear = itemDate.getFullYear();
                  const itemMonth = itemDate.getMonth();
                  const itemDay = itemDate.getDate();
                  const itemDateOnly = new Date(itemYear, itemMonth, itemDay);
                  if (f.filterType === 'Intervalo') {
                    const startStr = (f.value as any)?.start;
                    const endStr = (f.value as any)?.end;
                    if (!startStr || !endStr) return true;
                    const startParts = startStr.split('-');
                    const endParts = endStr.split('-');
                    const startDate = new Date(Number(startParts[0]), Number(startParts[1]) - 1, Number(startParts[2]));
                    const endDate = new Date(Number(endParts[0]), Number(endParts[1]) - 1, Number(endParts[2]));
                    return itemDateOnly >= startDate && itemDateOnly <= endDate;
                  } else {
                    const dateStr = String(f.value || '').trim();
                    if (!dateStr) return true;
                    let targetDate: Date | null = null;
                    const parts = dateStr.split('-');
                    if (parts.length === 3) {
                      targetDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                    } else {
                      const brParts = dateStr.split('/');
                      if (brParts.length === 3) {
                        targetDate = new Date(Number(brParts[2]), Number(brParts[1]) - 1, Number(brParts[0]));
                      }
                    }
                    if (!targetDate || isNaN(targetDate.getTime())) return true;
                    const type = f.filterType;
                    if (type === 'equals' || type === 'É igual a' || type === 'contains' || type === 'Igual a') return itemDateOnly.getTime() === targetDate.getTime();
                    else if (type === 'not' || type === 'É diferente de') return itemDateOnly.getTime() !== targetDate.getTime();
                    else if (type === 'gte' || type === 'É maior/igual que') return itemDateOnly >= targetDate;
                    else if (type === 'gt' || type === 'É maior que' || type === 'Depois de') return itemDateOnly > targetDate;
                    else if (type === 'lte' || type === 'É menor/igual que') return itemDateOnly <= targetDate;
                    else if (type === 'lt' || type === 'É menor que' || type === 'Antes de') return itemDateOnly < targetDate;
                    return true;
                  }
                } else if (
                  key === 'newLengthInMm' || key === 'oldLengthInMm' ||
                  key === 'newTotalWeight' || key === 'oldTotalWeight' ||
                  key === 'newQuantity' || key === 'oldQuantity'
                ) {
                  const recordItem = item as Record<string, any>;
                  const itemVal = recordItem[key] !== undefined && recordItem[key] !== null ? Number(recordItem[key]) : null;
                  const filterVal = Number(f.value);
                  if (isNaN(filterVal)) return true;
                  if (itemVal === null) return false;
                  const type = f.filterType;
                  if (type === 'equals' || type === 'É igual a' || type === 'Igual a') return Math.abs(itemVal - filterVal) < 1e-6;
                  else if (type === 'not' || type === 'É diferente de') return Math.abs(itemVal - filterVal) >= 1e-6;
                  else if (type === 'gt' || type === 'É maior que') return itemVal > filterVal;
                  else if (type === 'gte' || type === 'É maior/igual que') return itemVal >= filterVal;
                  else if (type === 'lt' || type === 'É menor que') return itemVal < filterVal;
                  else if (type === 'lte' || type === 'É menor/igual que') return itemVal <= filterVal;
                  return true;
                }
                return true;
              });
            });
          });
        }

        const allCombined = filteredCombined.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setLastReturnedCount(extractedMovements.length);
        setMovements(allCombined as any);
      } catch (error: any) {
        console.error('Erro ao buscar movimentações e OCs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMovements();
  }, [quantity, search, movementTypeFilter, refresh, appliedFilters, isSteel]);

  useEffect(() => {
    const fetchTotalMovements = async () => {
      try {
        const directionValues = getDirectionFilterValues(appliedFilters);
        if (directionValues.includes('ordem de compra')) {
          setTotalMovements(null);
          return;
        }
        const filtersStr = appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined;
        const response = await ApiService.movements.getTotal({
          filters: filtersStr,
          global: search,
          movementType: isSteel ? undefined : movementTypeFilter,
          fashionOnly: !isSteel,
        });

        if (response?.data != null) {
          const total = typeof response.data === 'number' ? response.data : response.data.total || response.data.count || null;
          setTotalMovements(total);
        }
      } catch (error) {
        console.error('Erro ao buscar total de movimentações:', error);
        setTotalMovements(null);
      }
    };

    fetchTotalMovements();
  }, [search, movementTypeFilter, refresh, appliedFilters, isSteel]);

  useEffect(() => {
    if (isSteel) return;
    let cancelled = false;
    const opIds = [
      ...new Set(
        movements
          .map((m) => fashionOpIdFromOrderName(String(m.orderName ?? '')))
          .filter((x): x is number => x != null),
      ),
    ];
    const insumoIds = [
      ...new Set(
        movements
          .map((m) => insumoResourceIdFromRaw(String(m.rawMaterialName ?? '')))
          .filter((x): x is number => x != null),
      ),
    ];

    (async () => {
      const [pedidos, insumos] = await Promise.all([
        loadPedidoLabelsForOpIds(opIds),
        loadInsumoNames(insumoIds),
      ]);
      if (cancelled) return;
      setPedidoByOpId((prev) => ({ ...prev, ...pedidos }));
      setInsumoNameById((prev) => ({ ...prev, ...insumos }));
    })();

    return () => {
      cancelled = true;
    };
  }, [movements]);

  const formatNumber = (value: number | undefined | null) => {
    if (value == null || typeof value !== 'number') return '-';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleMaterialClick = async (item: FashionProductionMovementRow) => {
    const raw = String(item.rawMaterialName ?? '');
    const insId = insumoResourceIdFromRaw(raw);
    const resourceId = item.fashionConsumedResourceId || insId;

    if (resourceId != null) {
      setStock({ id: resourceId, isResource: true } as any);
      setModalSection('viewResource');
      setModalActive(true);
      return;
    }

    const stockId = item.fashionConsumedStockId || item.stockId;
    const code = materialCodeFromRaw(raw);

    try {
      const res = await ApiService.stock.findAll({ global: code, size: 40 });
      const payload = extractAxiosData(res) as any;
      const list = Array.isArray(payload)
        ? payload
        : payload?.items ?? payload?.data ?? [];

      if (Array.isArray(list)) {
        const foundItem =
          (stockId ? list.find((s: any) => s.id === stockId) : null) ||
          list.find((s: any) => String(s?.Code?.code ?? s?.code ?? '') === code);

        if (foundItem) {
          setStock({
            ...foundItem,
            itemColorId: foundItem.itemColorId ?? null,
            color: foundItem.color ?? '',
            pantoneCode: foundItem.pantoneCode ?? '',
            pantoneHex: foundItem.pantoneHex ?? '',
          });
          setStockMode(isSteel ? 'normal' : 'fashion');
          setModalSection('newModal');
          setModalActive(true);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar estoque para visualização:', error);
    }
  };

  const hasMore = useMemo(() => {
    if (totalMovements !== null) {
      return movements.length < totalMovements && lastReturnedCount === quantity;
    }
    return lastReturnedCount === quantity && lastReturnedCount > 0;
  }, [movements.length, quantity, totalMovements, lastReturnedCount]);

  const columns = useMemo(
    () =>
      buildFashionMovementColumns({
        isSteel,
        pedidoByOpId,
        insumoNameById,
        formatNumber,
        onMaterialClick: handleMaterialClick,
      }),
    [isSteel, pedidoByOpId, insumoNameById],
  );

  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    productionMovementScreenKey,
    columns,
  );

  return (
    <TableContainer>
      <StandardDataTable
        columns={columns}
        data={movements}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        totalCount={totalMovements}
        onLoadMore={() => setQuantity((q: number) => q + 50)}
        hasMore={hasMore}
        isLoading={isLoading}
        countLabel='movimentações'
        exportFileName='movimentacoes'
        emptyMessage='Nenhuma movimentação encontrada'
        selectable={false}
        rowClassName={(item) => (isFashionMovementReverted(item) ? 'yellowLine' : '')}
        rowTitle={(item: any) =>
          item.updatedAt
            ? `Atualizado por ${item.UpdatedByUser?.name || item.user?.name || 'Sistema'} em ${format(new Date(item.updatedAt), 'dd/MM/yyyy - HH:mm')}h`
            : undefined
        }
      />
    </TableContainer>
  );
};

export default FashionProductionMovementTable;
